import React, { useEffect, useMemo, useState } from 'react';

const ADSENSE_SCRIPT_SRC = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=';
const DEFAULT_CLIENT = 'ca-pub-7605740481859762';
const DEFAULT_SLOT = '6416288621';

function ensureAdsenseScript(client) {
    if (!client || typeof document === 'undefined') return;

    const existing = document.querySelector(`script[data-adsense-client="${client}"]`);
    if (existing) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = `${ADSENSE_SCRIPT_SRC}${client}`;
    script.crossOrigin = 'anonymous';
    script.setAttribute('data-adsense-client', client);
    document.head.appendChild(script);
}

const GoogleAdBanner = ({
    slot,
    format = 'auto',
    layout = '',
    layoutKey = '',
    className = '',
    minHeight = 180,
    compact = false,
}) => {
    const [adRequested, setAdRequested] = useState(false);

    const client = process.env.REACT_APP_ADSENSE_CLIENT || DEFAULT_CLIENT;
    const effectiveSlot = slot || process.env.REACT_APP_ADSENSE_DEFAULT_SLOT || DEFAULT_SLOT;
    const isConfigured = Boolean(client && effectiveSlot);

    const adStyle = useMemo(() => ({
        display: 'block',
        width: '100%',
        minHeight: `${minHeight}px`,
    }), [minHeight]);

    useEffect(() => {
        if (!isConfigured) return;

        ensureAdsenseScript(client);

        const tryPushAd = () => {
            try {
                window.adsbygoogle = window.adsbygoogle || [];
                window.adsbygoogle.push({});
                setAdRequested(true);
            } catch (error) {
                console.warn('Google Ads init failed:', error);
            }
        };

        if (window.adsbygoogle && !adRequested) {
            tryPushAd();
            return;
        }

        const timer = window.setTimeout(() => {
            if (!adRequested) {
                tryPushAd();
            }
        }, 800);

        return () => window.clearTimeout(timer);
    }, [client, effectiveSlot, isConfigured, adRequested]);

    return (
        <div className={`group relative w-full overflow-hidden rounded-[24px] bg-[#0B1C2E] shadow-[0_18px_40px_rgba(0,0,0,0.28)] ring-1 ring-white/10 ${className}`}>
            <div className="absolute left-3 top-3 z-20 rounded-full bg-black/45 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white/80 backdrop-blur-sm">
                Publicidad
            </div>

            <div className="relative min-h-[300px] px-4 py-4 md:px-6 md:py-6">
                {isConfigured && (
                    <ins
                        className="adsbygoogle relative z-10"
                        style={adStyle}
                        data-ad-client={client}
                        data-ad-slot={effectiveSlot}
                        data-ad-format={format}
                        {...(layout ? { 'data-ad-layout': layout } : {})}
                        {...(layoutKey ? { 'data-ad-layout-key': layoutKey } : {})}
                        data-full-width-responsive="true"
                    />
                )}

                <div
                    className={`pointer-events-none ${isConfigured ? 'absolute inset-4 md:inset-6' : 'relative'} flex items-center overflow-hidden rounded-[20px] bg-[radial-gradient(circle_at_top_left,rgba(69,168,255,0.35),transparent_40%),linear-gradient(135deg,#10243A_0%,#163A5C_55%,#0C1726_100%)] text-left ${compact ? 'px-5 py-5 md:px-7 md:py-5' : 'px-6 py-7'}`}
                    aria-hidden={isConfigured}
                >
                    <div className={`relative z-10 ${compact ? 'max-w-3xl' : 'max-w-2xl'}`}>
                        <p className={`font-black uppercase tracking-[0.28em] text-[#9FD3FF] ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
                            Sponsor destacado
                        </p>
                        <h3 className={`mt-3 font-black uppercase leading-tight text-white ${compact ? 'text-xl md:text-3xl' : 'text-2xl md:text-4xl'}`}>
                            Bonos exclusivos y promos para jugadores nuevos
                        </h3>
                        <p className={`mt-3 max-w-2xl leading-relaxed text-white/78 ${compact ? 'text-xs md:text-sm' : 'text-sm md:text-base'}`}>
                            Accedé a beneficios especiales, premios de bienvenida y novedades destacadas de la comunidad.
                        </p>
                        <div className={`mt-5 inline-flex items-center rounded-full bg-[#FCE503] font-black uppercase tracking-[0.18em] text-[#122436] shadow-[0_10px_30px_rgba(252,229,3,0.25)] ${compact ? 'px-3.5 py-1.5 text-[10px]' : 'px-4 py-2 text-xs'}`}>
                            Ver promoción
                        </div>
                    </div>

                    <div className={`absolute rounded-full bg-[#FCE503]/18 blur-2xl ${compact ? '-right-6 -top-8 h-28 w-28' : '-right-10 -top-10 h-40 w-40'}`} />
                    <div className={`absolute rounded-full bg-[#2284DF]/25 blur-2xl ${compact ? 'bottom-0 right-2 h-24 w-24' : 'bottom-0 right-0 h-32 w-32'}`} />
                </div>
            </div>
        </div>
    );
};

export default GoogleAdBanner;
